import type { FlowExecutionType } from "@/types/entities/flows";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { formatDateTime } from "@/utils/dates";
import { getExecutionBadge } from ".";
import { useGetFile } from "@/queries/resolutions";

interface Props {
  execution: FlowExecutionType["node_executions"][number];
}

interface FileItemProps {
  fileId: string;
}

const FileItem = ({ fileId }: FileItemProps) => {
  const { data: file, isLoading } = useGetFile(fileId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm bg-neutral-silent rounded px-3 py-2">
        <Icon
          type="far"
          name="spinner-third"
          className="animate-spin text-body-quiet"
        />
        <span className="text-body-quiet text-sm">Cargando...</span>
      </div>
    );
  }

  if (!file) return null;

  const handleDownload = () => {
    window.open(file.url, "_blank");
  };

  return (
    <div className="flex items-center justify-between gap-2 bg-neutral-silent rounded px-3 py-2">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Icon
          type="far"
          name="paperclip"
          className="text-body-quiet shrink-0"
        />
        <div className="flex flex-col min-w-0">
          <span className="text-body text-sm truncate">{file.file_name}</span>
          <span className="text-body-quiet text-xs">
            {(file.size / 1024).toFixed(2)} KB
          </span>
        </div>
      </div>
      <Button variant="quiet" size="sm" onClick={handleDownload}>
        <Icon type="far" name="download" />
      </Button>
    </div>
  );
};

const ResolutionContent = ({ execution }: Props) => {
  const status = execution.inputs?.find((input) => input.name === "status")
    ?.value as string;

  const description = execution.inputs?.find(
    (input) => input.name === "description",
  )?.value as string;

  const fileInputs =
    execution.inputs?.filter(
      (input) => input.name === "files" && input.value,
    ) || [];

  const resolutionId = execution.outputs?.find(
    (output) => output.name === "resolution",
  )?.value as string;

  const getStatusBadge = (status: string) => {
    const statusMap: Record<
      string,
      { variant: "success" | "danger" | "warning" | "neutral"; label: string }
    > = {
      approved: { variant: "success", label: "Aprobado" },
      rejected: { variant: "danger", label: "Rechazado" },
      pending: { variant: "warning", label: "Pendiente" },
    };

    const config = statusMap[status] || { variant: "neutral", label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
      </div>

      <div className="border border-neutral rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Icon
            type="far"
            name="gavel"
            className="text-primary text-xl shrink-0 mt-1"
          />
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-body-loud">Resolución</span>
                {status && getStatusBadge(status)}
              </div>
              <span className="text-sm text-body-quiet">
                Creada: {formatDateTime(execution.started_at)}
              </span>
            </div>
          </div>
        </div>

        {description && (
          <div className="flex flex-col gap-1 border-t border-neutral pt-3 mt-1">
            <span className="text-body-quiet font-semibold text-sm">
              Descripción:
            </span>
            <p className="text-body whitespace-pre-wrap">{description}</p>
          </div>
        )}

        {fileInputs.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-neutral pt-3 mt-1">
            <span className="text-body-quiet font-semibold text-sm">
              Archivos adjuntos ({fileInputs.length}):
            </span>
            <div className="flex flex-col gap-2">
              {fileInputs.map((fileInput) => (
                <FileItem
                  key={fileInput.id}
                  fileId={fileInput.value as string}
                />
              ))}
            </div>
          </div>
        )}

        {resolutionId && (
          <div className="flex items-center gap-2 text-sm border-t border-neutral pt-3 mt-1">
            <span className="text-body-quiet">ID de la resolución:</span>
            <span className="text-body font-mono text-xs">{resolutionId}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResolutionContent;
